import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import {
    CheckCircle2,
    AlertCircle,
    Calendar,
    X,
    Zap,
    History as HistoryIcon,
} from "lucide-react";

export default function PaymentCyclesPage() {
    const [meta, setMeta] = useState(null);
    const [month, setMonth] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [historyFor, setHistoryFor] = useState(null); // student row

    useEffect(() => {
        api.get("/payment-cycles/meta").then((r) => {
            setMeta(r.data);
            setMonth(r.data.current_month);
        });
    }, []);

    const loadMonth = async (m) => {
        if (!m) return;
        setLoading(true);
        try {
            const { data } = await api.get("/payment-cycles", { params: { month: m } });
            setData(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (month) loadMonth(month);
    }, [month]);

    const toggle = async (row) => {
        const next = row.status === "paid" ? "due" : "paid";
        await api.post("/payment-cycles/mark", {
            student_id: row.student_id,
            month,
            status: next,
            amount: 1500,
        });
        toast.success(`${row.student_name}: ${month} → ${next.toUpperCase()}`);
        loadMonth(month);
    };

    const bulkMark = async (status) => {
        if (!window.confirm(`Mark ALL ${data.rows.length} students as ${status.toUpperCase()} for ${data.label}?`))
            return;
        await api.post("/payment-cycles/bulk-mark", { month, status, amount: 1500 });
        toast.success(`All ${data.rows.length} students marked ${status.toUpperCase()} for ${data.label}`);
        loadMonth(month);
    };

    const selectableMonths = useMemo(() => {
        if (!meta) return [];
        return meta.all_months.filter((m) => m.month <= meta.current_month);
    }, [meta]);

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Monthly Billing</p>
                        <h1 className="font-display text-5xl text-[#111111]">Payment Cycles</h1>
                        {meta && (
                            <p className="text-[#666666] mt-2">
                                Academic Year{" "}
                                <span className="font-mono font-bold text-[#111111]">
                                    {meta.academic_year}
                                </span>{" "}
                                · Boarding requires payment for every month up to and including{" "}
                                <span className="font-bold text-[#111111]">{data?.label || "current"}</span>
                                .
                            </p>
                        )}
                    </div>

                    <div className="flex gap-2 items-center">
                        <Calendar size={16} className="text-[#666666]" />
                        <select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            data-testid="cycle-month-selector"
                            className="bg-white border border-[#111111] px-4 py-2 text-sm font-bold focus:outline-none"
                        >
                            {selectableMonths.map((m) => (
                                <option key={m.month} value={m.month}>
                                    {m.label} {m.month === meta?.current_month ? "· Current" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {data && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white border border-[#E5E5E5] p-6">
                                <div className="label-caps mb-2">Total</div>
                                <div className="font-display text-5xl">{data.total}</div>
                                <div className="text-xs text-[#666666] mt-2">students for {data.label}</div>
                            </div>
                            <div className="bg-white border border-[#34C759]/30 p-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 size={16} className="text-[#34C759]" />
                                    <span className="label-caps">Paid</span>
                                </div>
                                <div className="font-display text-5xl text-[#34C759]">{data.paid}</div>
                                <div className="text-xs text-[#666666] mt-2">
                                    {data.total > 0
                                        ? Math.round((data.paid / data.total) * 100)
                                        : 0}
                                    % collection rate
                                </div>
                            </div>
                            <div className="bg-white border border-[#FF3B30]/30 p-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle size={16} className="text-[#FF3B30]" />
                                    <span className="label-caps">Due</span>
                                </div>
                                <div className="font-display text-5xl text-[#FF3B30]">{data.due}</div>
                                <div className="text-xs text-[#666666] mt-2">outstanding for this cycle</div>
                            </div>
                        </div>

                        <div className="flex gap-3 mb-4 flex-wrap">
                            <button
                                onClick={() => bulkMark("paid")}
                                data-testid="bulk-mark-paid"
                                className="bg-[#34C759] text-white font-bold px-5 py-2 text-sm uppercase tracking-wider hover:bg-[#2AB04A] flex items-center gap-2"
                            >
                                <Zap size={14} /> Mark All Paid
                            </button>
                            <button
                                onClick={() => bulkMark("due")}
                                data-testid="bulk-mark-due"
                                className="bg-white text-[#FF3B30] border border-[#FF3B30] font-bold px-5 py-2 text-sm uppercase tracking-wider hover:bg-[#FF3B30] hover:text-white flex items-center gap-2"
                            >
                                <AlertCircle size={14} /> Mark All Due
                            </button>
                        </div>

                        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                                    <tr>
                                        <th className="text-left font-bold py-3 px-4">Student</th>
                                        <th className="text-left font-bold py-3 px-4">Roll</th>
                                        <th className="text-left font-bold py-3 px-4">Department</th>
                                        <th className="text-left font-bold py-3 px-4">Status</th>
                                        <th className="text-left font-bold py-3 px-4">Amount</th>
                                        <th className="text-left font-bold py-3 px-4">Paid On</th>
                                        <th className="text-right font-bold py-3 px-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="cycles-table">
                                    {loading && (
                                        <tr>
                                            <td colSpan="7" className="p-8 text-center text-[#666666]">
                                                Loading…
                                            </td>
                                        </tr>
                                    )}
                                    {!loading &&
                                        data.rows.map((r) => (
                                            <tr
                                                key={r.student_id}
                                                className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]"
                                            >
                                                <td className="py-3 px-4 font-medium">{r.student_name}</td>
                                                <td className="py-3 px-4 font-mono text-xs">{r.roll_number}</td>
                                                <td className="py-3 px-4 text-[#666666]">{r.department}</td>
                                                <td className="py-3 px-4">
                                                    <button
                                                        onClick={() => toggle(r)}
                                                        data-testid={`cycle-toggle-${r.roll_number}`}
                                                        className={`font-bold px-2 py-1 text-xs uppercase tracking-wider border transition-colors ${
                                                            r.status === "paid"
                                                                ? "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30 hover:bg-[#34C759]/20"
                                                                : "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                                                        }`}
                                                    >
                                                        {r.status}
                                                    </button>
                                                </td>
                                                <td className="py-3 px-4 font-mono text-xs">
                                                    ₹{(r.amount || 0).toLocaleString("en-IN")}
                                                </td>
                                                <td className="py-3 px-4 font-mono text-xs text-[#666666]">
                                                    {r.paid_date
                                                        ? new Date(r.paid_date).toLocaleDateString()
                                                        : "—"}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <button
                                                        onClick={() => setHistoryFor(r)}
                                                        data-testid={`cycle-history-${r.roll_number}`}
                                                        className="text-xs font-bold uppercase tracking-wider border border-[#111111] px-3 py-1 hover:bg-[#111111] hover:text-white inline-flex items-center gap-2"
                                                    >
                                                        <HistoryIcon size={12} /> History
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {historyFor && (
                <HistoryModal
                    student={historyFor}
                    onClose={() => setHistoryFor(null)}
                    onChanged={() => loadMonth(month)}
                />
            )}
        </AdminLayout>
    );
}

function HistoryModal({ student, onClose, onChanged }) {
    const [data, setData] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/students/${student.student_id}/payment-history`);
        setData(data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [student.student_id]);

    const toggle = async (h) => {
        const next = h.status === "paid" ? "due" : "paid";
        await api.post("/payment-cycles/mark", {
            student_id: student.student_id,
            month: h.month,
            status: next,
            amount: 1500,
        });
        toast.success(`${h.label} → ${next.toUpperCase()}`);
        await load();
        onChanged();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white border-2 border-[#111111] w-full max-w-2xl animate-slide-up">
                <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                    <div>
                        <div className="font-display text-2xl">{student.student_name}</div>
                        <div className="font-mono text-xs text-[#666666]">
                            {student.roll_number} · Payment History
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="close-history"
                        className="p-1 hover:bg-[#F8F9FA]"
                    >
                        <X size={20} />
                    </button>
                </div>
                {!data && <div className="p-10 text-center text-[#666666]">Loading…</div>}
                {data && (
                    <div className="p-6">
                        <div
                            className={`border p-4 mb-4 ${
                                data.eligibility.eligible
                                    ? "bg-[#34C759]/10 border-[#34C759]/30"
                                    : "bg-[#FF3B30]/10 border-[#FF3B30]/30"
                            }`}
                        >
                            <p className="label-caps mb-1">Boarding Eligibility — AY {data.academic_year}</p>
                            {data.eligibility.eligible ? (
                                <p className="font-bold text-[#34C759]">
                                    ✓ Eligible — all required cycles paid
                                </p>
                            ) : (
                                <p className="font-bold text-[#FF3B30]">
                                    ✗ Blocked — {data.eligibility.unpaid_months.length} unpaid cycle(s)
                                </p>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {data.history
                                .filter((h) => h.is_past_or_current)
                                .map((h) => (
                                    <button
                                        key={h.month}
                                        onClick={() => toggle(h)}
                                        data-testid={`history-cell-${h.month}`}
                                        className={`p-3 text-left border transition-all ${
                                            h.status === "paid"
                                                ? "bg-[#34C759]/10 border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/20"
                                                : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/20"
                                        } ${h.is_current ? "ring-2 ring-[#111111] ring-offset-1" : ""}`}
                                    >
                                        <div className="font-mono text-xs text-[#666666] mb-1">
                                            {h.month}
                                        </div>
                                        <div className="font-display text-lg leading-none">{h.label}</div>
                                        <div className="text-xs font-bold uppercase tracking-wider mt-2">
                                            {h.status}
                                        </div>
                                    </button>
                                ))}
                        </div>
                        <p className="text-xs text-[#666666] mt-4">
                            Showing past and current cycles for AY {data.academic_year}. Click any cell to
                            toggle its status.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
